import { Injectable } from '@angular/core';
import { Observable, Subscriber, of } from 'rxjs';
import { LoginService } from './login.service';
import { User } from '../models/user';
import { ConnectionService } from '../../../services/connection/connection.service';
import { InMemoryUsers } from '../models/in-memory-users';

@Injectable()
export class LoginInMemoryService extends LoginService {

  private _inMemoryUsers = new InMemoryUsers();

  constructor(
    private connectionService: ConnectionService,
  ) {
    super();
  }

  login(login: string, password: string, database?: string): Observable<User> {
    return Observable.create((observer: Subscriber<User>) => {
      observer.next(this.findUserByLogin(login, password));
      observer.complete();
    });
  }

  get loggedInUser(): Observable<User> {
    if (!this.connectionService.isAuthorizationTokenExist) {
      return of(null);
    }
    return Observable.create((observer: Subscriber<User>) => {
      observer.next(this.findUserBySessionId(this.connectionService.authorizationToken));
      observer.complete();
    });
  }

  createUser(user: User): void {
    if (this._users.find(x => x.login === user.login)) {
      throw Error(`A user with this email: '${user.login}' already exists!`);
    }

    user.id = this._inMemoryUsers.newItemId;
    user.session_id = user.id.toString();
    this._users.push(user);
    this._saveUsers();
  }

  resetPassword(email: string): Observable<string> {
    return Observable.create((observer: Subscriber<string>) => {
      let user: User;
      for (const item of this._users) {
        if (item.login === email) {
          user = item;
          break;
        }
      }

      if (user == null) {
        observer.error(Error('Could not reset your password'));
        observer.complete();
        return;
      }

      user.password = '123';
      this._saveUsers();

      observer.next('An email has been sent with credentials to reset your password');
      observer.complete();
    });
  }

  resetPasswordByToken(token: string, password: string, confirmPassword: string): Observable<string> {
    return Observable.create((observer: Subscriber<string>) => {
      observer.next('Password was changed');
      observer.complete();
    });
  }

  changePassword(password: string, newPassword: string): Observable<boolean> {
    return Observable.create((observer: Subscriber<boolean>) => {
      return this.loggedInUser.subscribe(
        user => {
          if (user == null || user.password !== password) {
            observer.next(false);
            return;
          }

          user.password = newPassword;
          this._saveUsers();
          observer.next(true);
        },
        error => {
          observer.next(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  private findUserBySessionId(user_id: string): User {
    for (const item of this._users) {
      if (item.session_id === user_id) {
        return item;
      }
    }
    return null;
  }

  private findUserByLogin(login: string, password: string): User {
    for (const item of this._users) {
      if (item.login === login && item.password === password) {
        return item;
      }
    }
    return null;
  }

  private get _users(): User[] {
    return this._inMemoryUsers.items;
  }

  private _saveUsers() {
    this._inMemoryUsers.save();
  }
}
